// import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import Button from '../../../components/button';
import Table from '../../../components/table';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { COLORS } from '../../../constants/style/color';
import { TermAndConditionHeader } from '../../../constants/table-headers/termAndConditionHeader';
import {
  useBoolean,
  useGetTablePreferences,
  useGetTermAndCondition,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { TermsAndConditionsService } from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import {
  SettingSystemUserButton,
  SettingSystemUserHeader,
  SystemUserContainer,
} from '../system-users/systemUserStyled';
import TermAndConditionModel from './termAndConditionModel';
import { useQueryClient } from 'react-query';
import { isEmpty } from 'lodash';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { useNavigate } from 'react-router-dom';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';

interface Terms {
  id: string;
  name: string;
  documentContent: string;
  isActive: string;
  documentList: any;
  document: any[];
}

export const termAndConditionInitialValues = {
  data: InitialValuesForm.termAndCondition as Terms,
  id: '',
};

const CompanyTnC = (): JSX.Element => {
  const navigate = useNavigate();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [termAndConditionInitialData, setTermAndConditionInitialData] = useState<
    typeof termAndConditionInitialValues
  >(termAndConditionInitialValues);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { tableInfo, setTableInfo } = useDynamicTable();
  const [headerList, setHeaderList] = useState<string[]>([]);

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const { isLoading, handleRequest } = useWealthlaneCustomerApi();
  const {
    data: termAndConditionList,
    isLoading: isTermConditionsLoading,
    status: load,
  } = useGetTermAndCondition(handleRequest);
  const [dataRow, setDataRow] = useState<string>();
  const queryClient = useQueryClient();
  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'TERMS_CONDITIONS_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Tnc');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Tnc',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };
  const tableActions = [
    {
      action: 'View',
      onClick: (id: string, rowData: any) => {
        setModalStatus('View');
        openModal();
        getTermAndConditionData(id);
      },
    },
    {
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string, rowData: any) => {
        setModalStatus('Edit');
        openModal();
        getTermAndConditionData(id);
      },
    },
  ];

  const getTermAndConditionData = (id: string, status?: string, values?: any): void => {
    void handleRequest(
      TermsAndConditionsService.getApiCustomerApiAppTermsAndConditionsById(id)
    ).then((data: any) => {
      if (data != null) {
        const filteredData = {
          id: data.id,
          name: data.name,
          pageId: data.pageId,
          agreementStatement: data.agreementStatement,
          isActive: data.isActive === true ? 'Active' : 'InActive',
          documentContent: data.documentContent,
          document: [],
          documentList: [
            {
              file: {
                name: data?.fileName,
                uniqueName: data?.id,
              },
            },
          ],
        };

        setTermAndConditionInitialData({ id, data: filteredData });
      }
    });
  };

  const tableHeaderList = TermAndConditionHeader({ actions: schemaData?.columns, tableActions })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const tncHeader = (): any[] => {
    return TermAndConditionHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: tncHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        variant={'primary'}
        title={'Add Terms & Conditions'}
        icon={'add_circle_outline'}
        onClick={() => {
          setTermAndConditionInitialData(termAndConditionInitialValues);
          setModalStatus('Add');
          setDataRow('');
          openModal();
        }}
      />
    );
  };
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <SystemUserContainer>
      <SettingSystemUserHeader>
        <Heading variant='h3' title='Company T&C' />
        <Heading
          variant='body-s'
          title={`List of legal documents used in the Wealthlane system for the company.`}
          color={`${COLORS.Gray}`}
        />
      </SettingSystemUserHeader>

      {/* <SettingSystemUserButton className='col-span-2 flex justify-end'>
        <Button
          as='Create'
          variant={'primary'}
          title={'Add Terms & Conditions'}
          icon={'add_circle_outline'}
          onClick={() => {
            setTermAndConditionInitialData(termAndConditionInitialValues);
            setModalStatus('Add');
            setDataRow('');
            openModal();
          }}
        />
      </SettingSystemUserButton> */}

      {isModalOpen && (
        <TermAndConditionModel
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          modalStatus={modalStatus}
          setModalStatus={setModalStatus}
          termAndConditionInitialData={termAndConditionInitialData}
          isLoading={isLoading}
          tableInfo={tableInfo}
          setTableInfo={setTableInfo}
          dataRow={dataRow}
        />
      )}
      <DynamicTable
        schema={schema}
        data={termAndConditionList ?? []}
        totalCounts={1000}
        loading={load}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {/* <Table
        columns={TermAndConditionHeader(actions)}
        data={termAndConditionList ?? []}
        loading={load}
        showPagination={false}
        totalCounts={1000}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(CompanyTnC, false, axiosInstance);
